import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import { useAppSelector } from 'hooks/reduxHooks'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import type { PropsWithChildren, ReactNode } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

export const AccommodationIndividualSelectRoomOptionTileDetailsContainer = styled(VerticalSpacer)``

type Props = {
  /**
   * positive integer
   */
  packageNumber?: number
  tagLineLabel?: ReactNode
  /**
   * Optional label rendered above the option title (e.g. rebrand suggested-package Popular badge).
   */
  promotionLabel?: ReactNode
  stayPayRateTier?: App.StayPayTier
  cancellationPolicy?: ReactNode
  variant: 'stacked' | 'carousel'
} & ({
  roomPackage?: App.HotelPackage
  selectedLoyaltyTargetUpgrade?: App.Package
  promotion?: undefined
  rateName?: undefined
} | {
  roomPackage?: undefined
  selectedLoyaltyTargetUpgrade?: undefined
  promotion?: App.BedbankPromotion
  rateName?: undefined
} | {
  roomPackage?: undefined
  selectedLoyaltyTargetUpgrade?: undefined
  promotion?: undefined
  rateName?: string
})

function AccommodationIndividualSelectRoomOptionTileDetails(props: PropsWithChildren<Props>) {
  const {
    children,
    packageNumber,
    rateName,
    tagLineLabel,
    promotionLabel,
    roomPackage,
    promotion,
    stayPayRateTier,
    selectedLoyaltyTargetUpgrade,
    cancellationPolicy,
    variant,
  } = props

  const intl = useIntl()

  const name = roomPackage?.name ?? promotion?.name ?? rateName
  const targetUpgradeName = selectedLoyaltyTargetUpgrade?.name
  const displayTier = useAppSelector(getLoyaltyDisplayTier)

  const packageOptionLabel = packageNumber && variant === 'carousel'
    ? intl.formatMessage(
        {
          id: '7Mi5NsVG_iGEq3sXKnR1',
          defaultMessage: 'Package option {number}',
        },
        { number: packageNumber },
      )
    : undefined

  const memberUpgradeLabel = targetUpgradeName
    ? intl.formatMessage(
        {
          id: '2FjAmCuPs3J95IPfxrZx',
          defaultMessage: '{tierName} member upgrade',
        },
        { tierName: getLoyaltyTierDisplayName(displayTier) },
      )
    : undefined

  const upgradedToLabel = targetUpgradeName
    ? intl.formatMessage(
        {
          id: '-GJNF9Ml3Ajp89hqJ6kj',
          defaultMessage: 'Upgraded to {upgradeName}',
        },
        { upgradeName: targetUpgradeName },
      )
    : undefined

  const nameLabel = name && stayPayRateTier
    ? intl.formatMessage(
        {
          id: 'i48WznOmxU19y14-IWRy',
          defaultMessage: '{name}, {count, plural, one {# night free} other {# nights free}}',
        },
        { name, count: stayPayRateTier.freeNights },
      )
    : name

  const optionWithUpgradeContent = packageOptionLabel && memberUpgradeLabel && <FormattedMessage
    id="X9NTnTF_ZIvsC-akeZkJ"
    defaultMessage="{optionLabel} | <bold>{upgradeLabel}</bold>"
    
    values={{
      optionLabel: packageOptionLabel,
      upgradeLabel: memberUpgradeLabel,
      bold: (chunks: React.ReactNode) => <b>{chunks}</b>,
    }}
  />

  return <AccommodationIndividualSelectRoomOptionTileDetailsContainer gap={12}>
    {promotionLabel}
    <VerticalSpacer gap={4}>
      {(!!packageOptionLabel || !!memberUpgradeLabel) && <Caption variant="large">
        {optionWithUpgradeContent || packageOptionLabel || <b>{memberUpgradeLabel}</b>}
      </Caption>}
      {!!tagLineLabel && <LabelGroup>{tagLineLabel}</LabelGroup>}
      {!!targetUpgradeName && <BodyText variant="large" weight="bold">
        {upgradedToLabel}
      </BodyText>}
      {!!name && <BodyText
        variant="large"
        weight="bold"
        colour={selectedLoyaltyTargetUpgrade ? 'neutral-four' : 'normal-contrast'}
        strikeThrough={!!selectedLoyaltyTargetUpgrade}
      >
        {nameLabel}
      </BodyText>}
      {cancellationPolicy}
    </VerticalSpacer>
    {children}
  </AccommodationIndividualSelectRoomOptionTileDetailsContainer>
}

export default AccommodationIndividualSelectRoomOptionTileDetails
